/**
 * Los umbrales del banco, editables por el banco.
 *
 * Es la pantalla que convierte una dependencia de Fligoo en una capacidad del área: hasta acá
 * corregir una banda era pedirnos un despliegue. Tres cosas la ordenan, y ninguna es decorativa:
 *
 * - **Nada se publica sin ver el impacto.** Cambiar un umbral es una decisión de cartera, así que
 *   antes de publicar se muestra cuántos legajos cambian de calificación y cuáles. La cuenta es la
 *   misma que hará el sistema al publicar, no una aproximación.
 * - **Lo que no se puede guardar se dice antes.** Los pesos que no suman uno, una banda que deja un
 *   hueco: el error aparece al lado del campo, con qué corregir, y el botón de publicar no se
 *   habilita.
 * - **Las versiones no se editan.** Publicar crea una nueva; las viejas quedan legibles y se puede
 *   volver a cualquiera. Un informe de hace seis meses se sigue explicando con la suya.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, friendlyError } from "../api/client";
import type { PolicyPayload } from "../api/types";
import { RECOMMENDATIONS, label } from "../lib/labels";
import { formatDate, formatNumber } from "../lib/model";
import {
  Button,
  Chip,
  EmptyState,
  ErrorBanner,
  PageHeader,
  SectionTitle,
  Spinner,
  Table,
  Td,
  Th,
} from "../components/ui";

export default function Policy() {
  const queryClient = useQueryClient();
  const active = useQuery({ queryKey: ["policy"], queryFn: api.policy });
  const versions = useQuery({ queryKey: ["policies"], queryFn: api.policies });
  const calibration = useQuery({ queryKey: ["calibration"], queryFn: api.calibration });

  const [draft, setDraft] = useState<PolicyPayload | null>(null);
  const [reason, setReason] = useState("");
  const [problems, setProblems] = useState<string[]>([]);

  const preview = useMutation({
    mutationFn: (payload: PolicyPayload) => api.previewPolicy(payload),
    onError: (e) => setProblems(problemsOf(e)),
    onSuccess: () => setProblems([]),
  });
  const publish = useMutation({
    mutationFn: ({ payload, why }: { payload: PolicyPayload; why: string }) =>
      api.publishPolicy(payload, why),
    onError: (e) => setProblems(problemsOf(e)),
    onSuccess: async () => {
      setDraft(null);
      setReason("");
      setProblems([]);
      preview.reset();
      await queryClient.invalidateQueries({ queryKey: ["policy"] });
      await queryClient.invalidateQueries({ queryKey: ["policies"] });
    },
  });

  const current = draft ?? active.data?.payload ?? null;
  const dirty = draft !== null;
  const totals = useMemo(() => weights(current), [current]);

  if (active.isError) return <ErrorBanner {...friendlyError(active.error)} />;
  if (!current || !active.data) return <Spinner label="Cargando la política…" />;

  function edit(change: (policy: PolicyPayload) => void) {
    const next: PolicyPayload = JSON.parse(JSON.stringify(current));
    change(next);
    setDraft(next);
    preview.reset();
  }

  return (
    <>
      <PageHeader
        title="Política de crédito"
        subtitle={
          <>
            <span>
              Versión <strong className="text-ink">{active.data.version}</strong>
            </span>
            {active.data.payload.provisional ? (
              <Chip text="Provisoria" tone="warn" />
            ) : (
              <Chip text="Validada" tone="good" />
            )}
            <span>
              {active.data.author_name ? `Publicada por ${active.data.author_name}` : ""}{" "}
              {formatDate(active.data.created_at)}
            </span>
          </>
        }
        actions={
          dirty ? (
            <>
              <Button
                variant="primary"
                disabled={preview.isPending}
                onClick={() => preview.mutate(current)}
              >
                {preview.isPending ? "Calculando…" : "Ver impacto"}
              </Button>
              <Button onClick={() => { setDraft(null); setProblems([]); preview.reset(); }}>
                Descartar cambios
              </Button>
            </>
          ) : null
        }
      />
      <div className="my-5 border-b border-line" />

      {problems.length ? (
        <div className="mb-5 rounded-lg border border-bad-line bg-bad-bg p-4">
          <div className="text-sm font-semibold text-bad">
            Esto no se puede publicar todavía
          </div>
          <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm">
            {problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {preview.data ? (
        <div className="mb-6 rounded-xl border border-line p-4">
          <SectionTitle hint="Es la misma cuenta que hará el sistema al publicar, sobre los legajos activos.">
            Qué pasaría con la cartera
          </SectionTitle>
          <p className="text-sm">
            Sobre <strong>{preview.data.evaluated}</strong> legajo(s) evaluados,{" "}
            <strong>{preview.data.changed}</strong> cambian de calificación
            {preview.data.changed ? (
              <>
                {" "}— <span className="text-bad">{preview.data.worse} empeoran</span> y{" "}
                <span className="text-good">{preview.data.better} mejoran</span>
              </>
            ) : null}
            .
          </p>

          {preview.data.cases.length ? (
            <div className="mt-3">
              <Table>
                <thead>
                  <tr>
                    <Th>Empresa</Th>
                    <Th>Hoy</Th>
                    <Th>Quedaría</Th>
                    <Th>Recomendación</Th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.cases.slice(0, 25).map((c) => (
                    <tr key={c.case_id}>
                      <Td className="font-medium">{c.legal_name}</Td>
                      <Td>
                        {c.grade_before ?? "—"}
                        <span className="ml-1 text-muted">
                          ({c.score_before === null ? "—" : formatNumber(c.score_before, 1)})
                        </span>
                      </Td>
                      <Td className={c.worse ? "text-bad" : "text-good"}>
                        {c.grade_after ?? "—"}
                        <span className="ml-1 opacity-75">
                          ({c.score_after === null ? "—" : formatNumber(c.score_after, 1)})
                        </span>
                      </Td>
                      <Td className="text-muted">
                        {label(RECOMMENDATIONS, c.recommendation_before)} →{" "}
                        {label(RECOMMENDATIONS, c.recommendation_after)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : null}

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="flex-1 text-sm">
              Motivo del cambio
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Sesión de calibración con Riesgo, 10/09"
                className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </label>
            <Button
              variant="primary"
              disabled={!reason.trim() || publish.isPending}
              onClick={() => publish.mutate({ payload: current, why: reason })}
            >
              {publish.isPending ? "Publicando…" : `Publicar ${current.version}`}
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted">
            La versión publicada no se edita: los cambios siguientes crean una nueva. Los informes ya
            emitidos se siguen explicando con la política con la que se calcularon.
          </p>
        </div>
      ) : null}

      {/* --- lo que dicen las decisiones ----------------------------------------------------- */}

      {calibration.data && calibration.data.decided > 0 ? (
        <div className="mb-6">
          <SectionTitle
            hint={`Sobre ${calibration.data.decided} legajo(s) decididos. Hacen falta al menos ${calibration.data.min_cases} desacuerdos en la misma banda para llamarlo sistemático.`}
          >
            Lo que dicen las decisiones del área
          </SectionTitle>

          <p className="text-sm">
            El analista terminó donde apuntaba la política en{" "}
            <strong>{calibration.data.agreed}</strong> de{" "}
            <strong>{calibration.data.decided}</strong> casos
            {calibration.data.agreement !== null ? (
              <> ({Math.round(calibration.data.agreement * 100)}%)</>
            ) : null}
            .
          </p>

          {calibration.data.findings.length ? (
            <ul className="mt-3 space-y-2">
              {calibration.data.findings.slice(0, 6).map((f) => (
                <li
                  key={`${f.indicator_key}-${f.band}`}
                  className={`rounded-lg border p-3 ${f.systematic ? "border-warn-line bg-warn-bg" : "border-line"}`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm">
                      <strong>{f.indicator}</strong>
                      <span className="text-muted">
                        {" "}· banda «{f.band}» · {f.component}
                      </span>
                    </span>
                    <span className="text-sm tabular-nums text-muted">
                      {f.disagreements} de {f.total} decisiones
                    </span>
                  </div>
                  <p className="mt-1 text-sm">
                    En esta banda el área viene decidiendo{" "}
                    <strong>{f.direction || "distinto"}</strong> que la política
                    {f.systematic ? ", de forma sistemática" : ""}.{" "}
                    <span className="text-muted">
                      {f.cases
                        .slice(0, 3)
                        .map((c) => c.legal_name)
                        .join(", ")}
                      {f.cases.length > 3 ? ` y ${f.cases.length - 3} más` : ""}
                    </span>
                  </p>
                  {f.systematic ? (
                    <button
                      onClick={async () => {
                        const suggestion = await api.proposal(f.indicator_key, f.band);
                        setDraft(suggestion.payload);
                        setProblems([]);
                        preview.reset();
                      }}
                      className="mt-1.5 text-sm text-primary underline underline-offset-2"
                    >
                      Proponer el ajuste de esta banda
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-muted">
              Todavía no hay desacuerdos que analizar: la política y el criterio del área coinciden
              en todo lo decidido hasta ahora.
            </p>
          )}
          <p className="mt-2 text-xs text-muted">
            Una propuesta nunca se publica sola: carga el cambio en el editor de abajo, y lo firma
            una persona con su motivo.
          </p>
        </div>
      ) : null}

      {/* --- el editor --------------------------------------------------------------------- */}

      <div className="flex flex-wrap items-end gap-4">
        <label className="text-sm">
          Nombre de esta versión
          <input
            value={current.version}
            onChange={(e) => edit((p) => (p.version = e.target.value))}
            className="mt-1 block w-56 rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />
        </label>
        <label className="text-sm">
          Piso de cobertura
          <input
            type="number"
            step="0.05"
            min="0"
            max="1"
            value={current.min_coverage}
            onChange={(e) => edit((p) => (p.min_coverage = Number(e.target.value)))}
            className="mt-1 block w-28 rounded-lg border border-line px-3 py-2 text-sm tabular-nums focus:border-primary focus:outline-none"
          />
          <span className="mt-0.5 block text-xs text-muted">
            Debajo de esto el puntaje se informa como parcial
          </span>
        </label>
        <div className={`text-sm ${totals.ok ? "text-good" : "text-bad"}`}>
          Pesos de los componentes: {formatNumber(totals.total * 100, 0)}%
          {totals.ok ? "" : " — tienen que sumar 100%"}
        </div>
      </div>

      {current.components.map((component, ci) => (
        <div key={component.key} className="mt-6 rounded-xl border border-line p-4">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-base font-semibold">{component.label}</h3>
            <label className="text-sm text-muted">
              peso
              <input
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={component.weight}
                onChange={(e) =>
                  edit((p) => (p.components[ci]!.weight = Number(e.target.value)))
                }
                className="ml-2 w-20 rounded-lg border border-line px-2 py-1 text-sm tabular-nums text-ink focus:border-primary focus:outline-none"
              />
            </label>
            <span className="text-sm text-muted">{component.question}</span>
          </div>

          {component.indicators.map((indicator, ii) => (
            <div key={indicator.key} className="mt-3 border-t border-line pt-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium">{indicator.label}</span>
                <label className="text-xs text-muted">
                  peso dentro del componente
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    value={indicator.weight}
                    onChange={(e) =>
                      edit(
                        (p) =>
                          (p.components[ci]!.indicators[ii]!.weight = Number(e.target.value)),
                      )
                    }
                    className="ml-2 w-20 rounded-lg border border-line px-2 py-1 text-sm tabular-nums text-ink focus:border-primary focus:outline-none"
                  />
                </label>
              </div>

              <div className="mt-2 flex flex-wrap gap-3">
                {indicator.bands.map((band, bi) => (
                  <div key={bi} className="rounded-lg border border-line px-3 py-2">
                    <div className="text-[0.7rem] text-muted">{band.label}</div>
                    <div className="mt-1 flex items-center gap-1.5 text-sm">
                      <span className="text-muted">
                        {band.upper === null ? "resto" : "hasta"}
                      </span>
                      {band.upper === null ? null : (
                        <input
                          type="number"
                          step="0.1"
                          value={band.upper}
                          onChange={(e) =>
                            edit(
                              (p) =>
                                (p.components[ci]!.indicators[ii]!.bands[bi]!.upper = Number(
                                  e.target.value,
                                )),
                            )
                          }
                          className="w-20 rounded border border-line px-1.5 py-0.5 text-sm tabular-nums focus:border-primary focus:outline-none"
                        />
                      )}
                      <span className="text-muted">→</span>
                      <input
                        type="number"
                        step="5"
                        min="0"
                        max="100"
                        value={band.points}
                        onChange={(e) =>
                          edit(
                            (p) =>
                              (p.components[ci]!.indicators[ii]!.bands[bi]!.points = Number(
                                e.target.value,
                              )),
                          )
                        }
                        className="w-16 rounded border border-line px-1.5 py-0.5 text-sm tabular-nums focus:border-primary focus:outline-none"
                      />
                      <span className="text-muted">pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      <SectionTitle hint="Hechos que limitan la calificación por sí solos, sin importar el resto.">
        Topes
      </SectionTitle>
      <Table>
        <thead>
          <tr>
            <Th>Hecho</Th>
            <Th right>Limita el puntaje a</Th>
          </tr>
        </thead>
        <tbody>
          {current.knockouts.map((knockout, ki) => (
            <tr key={knockout.key}>
              <Td className="font-medium">{knockout.label}</Td>
              <Td right>
                <input
                  type="number"
                  step="5"
                  min="0"
                  max="100"
                  value={knockout.caps_at}
                  onChange={(e) =>
                    edit((p) => (p.knockouts[ki]!.caps_at = Number(e.target.value)))
                  }
                  className="w-20 rounded border border-line px-1.5 py-0.5 text-sm tabular-nums focus:border-primary focus:outline-none"
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      <SectionTitle>Escala</SectionTitle>
      <Table>
        <thead>
          <tr>
            <Th>Grado</Th>
            <Th right>Desde</Th>
            <Th>Lectura</Th>
            <Th>Sugerencia</Th>
          </tr>
        </thead>
        <tbody>
          {current.ratings.map((rating, ri) => (
            <tr key={rating.grade}>
              <Td className="font-semibold">{rating.grade}</Td>
              <Td right>
                <input
                  type="number"
                  step="5"
                  min="0"
                  max="100"
                  value={rating.min_score}
                  onChange={(e) =>
                    edit((p) => (p.ratings[ri]!.min_score = Number(e.target.value)))
                  }
                  className="w-20 rounded border border-line px-1.5 py-0.5 text-sm tabular-nums focus:border-primary focus:outline-none"
                />
              </Td>
              <Td className="text-muted">{rating.label}</Td>
              <Td className="text-muted">{label(RECOMMENDATIONS, rating.recommendation)}</Td>
            </tr>
          ))}
        </tbody>
      </Table>

      <SectionTitle hint="Publicar crea una versión nueva; las anteriores quedan y se puede volver a cualquiera.">
        Historial
      </SectionTitle>
      {versions.data?.length ? (
        <Table>
          <thead>
            <tr>
              <Th>Versión</Th>
              <Th>Publicada</Th>
              <Th>Por</Th>
              <Th>Motivo</Th>
              <Th>{""}</Th>
            </tr>
          </thead>
          <tbody>
            {versions.data.map((v) => (
              <tr key={v.version}>
                <Td className="font-medium">
                  {v.version}
                  {v.is_active ? <Chip text="En uso" tone="good" /> : null}
                </Td>
                <Td className="text-muted">{formatDate(v.created_at)}</Td>
                <Td className="text-muted">{v.author_name ?? "—"}</Td>
                <Td className="text-muted">{v.reason || "—"}</Td>
                <Td right>
                  {v.is_active ? null : (
                    <button
                      onClick={async () => {
                        await api.activatePolicy(v.version);
                        await queryClient.invalidateQueries({ queryKey: ["policy"] });
                        await queryClient.invalidateQueries({ queryKey: ["policies"] });
                      }}
                      className="text-primary underline underline-offset-2"
                    >
                      Volver a esta
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyState title="Todavía no hay versiones publicadas" text="" />
      )}
    </>
  );
}

function weights(policy: PolicyPayload | null) {
  const total = (policy?.components ?? []).reduce((sum, c) => sum + (c.weight || 0), 0);
  return { total, ok: Math.abs(total - 1) < 1e-6 };
}

/** Los problemas que devuelve la API vienen en `detail`, que es una lista de frases para el editor. */
function problemsOf(error: unknown): string[] {
  const detail = (error as { raw?: string })?.raw;
  if (!detail) return [friendlyError(error).message];
  try {
    const parsed = JSON.parse(detail);
    if (Array.isArray(parsed?.detail)) return parsed.detail.map(String);
    if (typeof parsed?.detail === "string") return [parsed.detail];
  } catch {
    /* el cuerpo no era JSON: cae al mensaje genérico */
  }
  return [friendlyError(error).message];
}
